import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
    doc,
    setDoc,
    updateDoc,
    serverTimestamp,
    getDoc,
    collection,
    Timestamp
} from "firebase/firestore";
import { soloNome } from "../utils/nomi";


const QuizContext = createContext();

export const useQuiz = () => useContext(QuizContext);

export const QuizProvider = ({ children }) => {

    const [requestId, setRequestId] = useState(null);
    const [request, setRequest] = useState({});
    const [loadingDraft, setLoadingDraft] = useState(true);

    const draftCreationPromise = useRef(null);

    // ---------------------------
    // RECOVERY BOZZA
    // ---------------------------
    useEffect(() => {
        const savedId = localStorage.getItem("draftRequestId");
        if (!savedId) {
            setLoadingDraft(false);
            return;
        }

        const loadDraft = async () => {
            try {
                const ref = doc(db, "richieste_servizio", savedId);
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    const data = snap.data();
                    // Se il draft salvato non è più una bozza anonima libera
                    // (es. già pubblicata in un test/sessione precedente), non è
                    // più valido per questa sessione: va scartato, non riusato.
                    if (data.isDraft === true && data.id_richiedente === null) {
                        setRequestId(savedId);
                        setRequest(data);
                    } else {
                        localStorage.removeItem("draftRequestId");
                    }
                } else {
                    localStorage.removeItem("draftRequestId");
                }
            } catch (e) {
                console.error("Errore recovery draft", e);
                // Cancelliamo il draft salvato SOLO se l'errore dice esplicitamente
                // "permesso negato" — cioè il documento esiste ma non è più una
                // bozza leggibile in forma anonima. Un errore di rete/offline/
                // timeout NON significa che il draft sia invalido: in quel caso
                // lasciamo l'id in localStorage così un prossimo tentativo (refresh,
                // tornata la connessione) può ancora recuperarlo, invece di far
                // ripartire l'utente da zero per un problema temporaneo.
                if (e.code === "permission-denied") {
                    localStorage.removeItem("draftRequestId");
                }
            } finally {
                setLoadingDraft(false);
            }
        };

        loadDraft();
    }, []);

    // ---------------------------
    // CREA BOZZA (STEP 0)
    // ---------------------------
    const createDraft = async (cityData) => {

        if (requestId) {
            return requestId;
        }

        if (draftCreationPromise.current) {
            return draftCreationPromise.current;
        }

        const attempt = _createDraftAttempt(cityData);
        draftCreationPromise.current = attempt;

        try {
            return await attempt;
        } finally {
            draftCreationPromise.current = null;
        }
    };

    const _createDraftAttempt = async (cityData) => {

        const ref = doc(collection(db, "richieste_servizio"));

        const draft = {
            schema_version: "request_v1",

            tipo: "broadcast",
            categoria: "assistance",

            stato: "draft",
            stato_reason: null,
            stato_updated_at: serverTimestamp(),

            visibile_operatori: false,
            publishedAt: null,

            id_richiedente: null,
            id_destinatario: null,
            operatore_assegnato: null,

            timezone: "Europe/Rome",

            service_start_utc: null,
            service_end_utc: null,

            pricing: {
                modello: null,
                prezzo_orario: null,
                ore: null,
                totale_proposto: null,
                valuta: "EUR"
            },

            location: {
                city: cityData.city,
                // Nomi campo in italiano: coerenti con come location.provincia /
                // location.regione vengono letti/scritti ovunque nel resto della
                // app (Step5, RegistrazioneRapida, CRM, ProfiloOperatore,
                // ProfiloUtente, HomePage, MappaOperatori...). Prima qui si
                // scriveva "province"/"region" (inglese): la bozza iniziale
                // finiva con due campi orfani che nessun altro punto del codice
                // leggeva mai, e provincia/regione restavano vuoti finché non
                // veniva chiamato updateRequest da Step5.
                provincia: cityData.provincia,
                regione: cityData.regione,
                address_line: null,
                lat: cityData.lat,
                lng: cityData.lng
            },

            contact: {
                phone: null
            },

            details: {
                description: null,
                notes: null
            },

            requirements: {},

            type_payload: {},

            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),

            isDraft: true,

            expiresAt: Timestamp.fromDate(
                new Date(Date.now() + 48 * 60 * 60 * 1000)
            )
        };

        localStorage.setItem("draftRequestId", ref.id);
        setRequestId(ref.id);
        setRequest(draft);

        try {
            await setDoc(ref, draft);
        } catch (e) {
            console.error("Errore createDraft Firestore:", e);
            localStorage.removeItem("draftRequestId");
            setRequestId(null);
            throw e;
        }

        return ref.id;
    };

    // ---------------------------
    // UPDATE STEP DATA
    // ---------------------------
    const updateRequest = async (data) => {
        if (!requestId) return;

        // Guard #1: richiesta già pubblicata → blocca qualunque scrittura.
        // Evita che un utente tornato indietro nel wizard (browser back)
        // possa risuscitare/modificare una richiesta già finalizzata.
        if (request?.isDraft === false) {
            console.warn("updateRequest: richiesta già pubblicata, scrittura ignorata", requestId);
            return;
        }

        const ref = doc(db, "richieste_servizio", requestId);

        setRequest(prev => {
            const merged = {
                ...prev,
                ...data,

                location: data.location
                    ? { ...prev.location, ...data.location }
                    : prev.location,

                details: data.details
                    ? { ...prev.details, ...data.details }
                    : prev.details,

                contact: data.contact
                    ? { ...prev.contact, ...data.contact }
                    : prev.contact
            };

            return merged;
        });

        const firestorePayload = {
            updated_at: serverTimestamp()
        };

        // Fix #3: stato_updated_at si scrive solo se `stato` è effettivamente
        // nel payload — evita che ogni updateRequest (anche per un semplice
        // step_corrente) resetti il timestamp di cambio stato.
        if ("stato" in data) {
            firestorePayload.stato_updated_at = serverTimestamp();
        }

        if (data.location) {
            Object.entries(data.location).forEach(([key, value]) => {
                firestorePayload[`location.${key}`] = value;
            });
        }

        if (data.details) {
            Object.entries(data.details).forEach(([key, value]) => {
                firestorePayload[`details.${key}`] = value;
            });
        }

        if (data.contact) {
            Object.entries(data.contact).forEach(([key, value]) => {
                firestorePayload[`contact.${key}`] = value;
            });
        }

        Object.entries(data).forEach(([key, value]) => {
            if (!["location", "details", "contact"].includes(key)) {
                firestorePayload[key] = value;
            }
        });

        await updateDoc(ref, firestorePayload);
    };

    // ---------------------------
    // FINALIZZAZIONE DOPO LOGIN
    // ---------------------------
    const finalizeRequest = async (user) => {

        const id = requestId || localStorage.getItem("draftRequestId");
        if (!id) return;

        const ref = doc(db, "richieste_servizio", id);

        const updates = {
            id_richiedente: user.uid,

            utente_snapshot: {
                // solo il nome: displayName di Google contiene anche il cognome
                nome: soloNome(user.displayName),
                foto: user.photoURL || null,
                telefono: request?.contact?.phone || null
            },

            stato: "published",
            stato_updated_at: serverTimestamp(),

            visibile_operatori: true,
            publishedAt: serverTimestamp(),

            isDraft: false,
            signupCompletedAt: serverTimestamp(),

            updated_at: serverTimestamp()
        };

        await updateDoc(ref, updates);

        // Allinea lo stato locale a quello appena scritto su Firestore.
        // Senza questo, request.isDraft/publishedAt restano quelli vecchi in
        // memoria finché il componente non fa un reload/refetch, e sia il
        // Guard #1 di updateRequest sia useRedirectIfPublished non si
        // accorgono che la richiesta è stata pubblicata.
        setRequest(prev => ({
            ...prev,
            ...updates,
            stato_updated_at: new Date(),
            publishedAt: new Date(),
            signupCompletedAt: new Date(),
            updated_at: new Date()
        }));

        localStorage.removeItem("draftRequestId");
    };

    return (
        <QuizContext.Provider
            value={{
                request,
                requestId,
                createDraft,
                updateRequest,
                finalizeRequest,
                loadingDraft
            }}
        >
            {children}
        </QuizContext.Provider>
    );
};